import classNames from "classnames";
import {
  CSSProperties,
  DetailedHTMLProps,
  ElementType,
  ForwardedRef,
  ReactNode,
  forwardRef,
  memo,
} from "react";
import { StyleProps, styleClasses } from "./styles";
import * as textCss from "./text.module.css";
import { TextAlignValue } from "./theme/typography";

type TextElement =
  | "p"
  | "span"
  | "div"
  | "code"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6";

export interface TextProps extends StyleProps {
  textOverflow?: CSSProperties["textOverflow"];
  whiteSpace?: CSSProperties["whiteSpace"];
  textAlign?: TextAlignValue;
  fontFamily?: "inherit" | "text" | "monospace";
}

export const Text = memo(
  forwardRef(function Text(
    props: Omit<TextProps, "lineHeight"> & {
      children: ReactNode;
      as?: TextElement;
      "data-testid"?: string;
    } & DetailedHTMLProps<
        React.HTMLAttributes<HTMLSpanElement>,
        HTMLSpanElement
      >,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const As: TextElement = props.as ?? "p";
    return (
      <As
        className={classNames(
          textCss.text,
          styleClasses({ ...props, fontFamily: props.fontFamily ?? "inherit" }),
        )}
        data-testid={props["data-testid"]}
        onClick={props.onClick}
        // TODO(paul): move to style
        // I am too afraid to fix this now
        // overflow={props.overflow}
        ref={ref}
        style={{
          textOverflow: props.textOverflow,
          whiteSpace: props.whiteSpace,
          color: props.color,
          ...props.style,
        }}
      >
        {props.children}
      </As>
    );
  }),
);

type HeaderElement =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "div"
  | ElementType;

export const Header = memo(function Header(
  props: Omit<TextProps, "fontFamily"> & {
    children: ReactNode;
    as?: HeaderElement;
    fontFamily?: "inherit";
    "data-testid"?: string;
  } & DetailedHTMLProps<
      React.HTMLAttributes<HTMLHeadingElement>,
      HTMLHeadingElement
    >,
) {
  const As: ElementType = props.as ?? "h1";
  return (
    <As
      className={classNames(
        textCss.text,
        styleClasses({
          ...props,
          fontFamily: props.fontFamily ?? "header",
          textColor: props.textColor ?? "primary",
          isHeader: true,
        }),
      )}
      data-testid={props["data-testid"]}
      style={{
        textOverflow: props.textOverflow,
        overflow: props.overflow,
        ...props.style,
      }}
    >
      {props.children}
    </As>
  );
});
